<template>
  <div>
    <SectionHeader>
      <template #icon><Cloud class="w-3.5 h-3.5 text-[hsl(var(--water-blue))]" /></template>
      {{ $t('routeDetails.weather.title') }}
      <template #action>
        <button
          @click="showInfoModal = true"
          class="text-ink2 hover:text-[hsl(var(--foreground))] transition-colors"
          :aria-label="$t('routeDetails.weather.infoTitle')"
        >
          <Info class="w-3.5 h-3.5" />
        </button>
      </template>
    </SectionHeader>
    <p v-if="!loading && !days.length" data-weather-unavailable class="mx-4 mb-3 flex items-center gap-2 text-[13px] text-muted-foreground">
      <CloudOff class="w-5 h-5 shrink-0" aria-hidden="true" />{{ $t('routeDetails.weather.unavailable') }}
    </p>
    <div v-else class="mx-3 mb-3 rounded-lg border border-border overflow-hidden">
      <div v-if="loading" class="p-3 text-[12px] text-ink2">{{ $t('routeDetails.weather.loading') }}</div>
      <template v-else-if="days.length">
        <div class="flex overflow-x-auto scrollbar-hide gap-1 p-2 border-b border-[hsl(var(--border)/0.6)]">
          <button
            v-for="(day, idx) in days"
            :key="day.date"
            @click="selectedDayIdx = idx"
            class="shrink-0 flex flex-col items-center gap-0.5 px-2.5 py-1.5 rounded-md text-[11px] font-semibold transition-colors"
            :class="selectedDayIdx === idx
              ? 'bg-[hsl(var(--primary))] text-white'
              : 'hover:bg-[hsl(var(--muted)/0.6)] text-[hsl(var(--foreground))]'"
          >
            <span>{{ dayLabel(day.date, idx) }}</span>
            <span class="text-[14px] leading-none">{{ weatherInfo(day.code).emoji }}</span>
          </button>
        </div>
        <div v-if="selectedDay" class="p-3">
          <div class="flex items-center justify-between mb-1.5">
            <span class="text-[13px] font-semibold">{{ weatherInfo(selectedDay.code).label }}</span>
            <span class="text-[13px] font-semibold">{{ Math.round(selectedDay.tmin) }}° / {{ Math.round(selectedDay.tmax) }}°</span>
          </div>
          <p class="text-[11px] text-ink2 mb-2">{{ $t('routeDetails.weather.windUpTo') }} {{ Math.round(selectedDay.windMax) }} {{ $t('units.ms') }}</p>
          <p class="text-[12px] text-[hsl(var(--foreground)/0.8)] leading-snug">{{ advisory(selectedDay.code) }}</p>
        </div>
        <p class="px-3 pb-2.5 text-[10px] text-ink2">{{ $t('routeDetails.weather.forecastSource') }}</p>
      </template>
    </div>

    <InfoModal v-if="showInfoModal" :title="$t('routeDetails.weather.infoTitle')" @close="showInfoModal = false">
      {{ $t('routeDetails.weather.infoText') }}
    </InfoModal>
  </div>
</template>

<script setup lang="ts">
import { routeCentroid } from "../utils/routeCentroid";
// Используется RouteDetailsPanel.vue и HikeCardView.vue (публичная страница
// "Поделиться") - один и тот же прогноз для маршрута не должен строиться
// дважды. Усредняет route.coordinates в одну точку и обращается к
// Open-Meteo напрямую (без бэкенда и авторизации - подходит и для публичной страницы).
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { Cloud, CloudOff, Info } from "lucide-vue-next";
import type { RouteMapItem } from "../types/routes";
import SectionHeader from "./SectionHeader.vue";
import InfoModal from "./InfoModal.vue";

const props = defineProps<{
  route: RouteMapItem | null;
}>();

const { t, locale } = useI18n();

interface WeatherDay {
  date: string;
  code: number;
  tmin: number;
  tmax: number;
  windMax: number;
}

const days = ref<WeatherDay[]>([]);
const loading = ref(true);
const selectedDayIdx = ref(0);
const showInfoModal = ref(false);

const selectedDay = computed(() => days.value[selectedDayIdx.value] || null);

const WEATHER_EMOJI: Record<number, string> = {
  0: "☀️", 1: "🌤️", 2: "⛅", 3: "☁️", 45: "🌫️", 48: "🌫️",
  51: "🌦️", 53: "🌦️", 55: "🌦️", 61: "🌧️", 63: "🌧️", 65: "🌧️",
  66: "🌧️", 67: "🌧️", 71: "❄️", 73: "❄️", 75: "❄️", 77: "❄️",
  80: "🌧️", 81: "🌧️", 82: "🌧️", 85: "🌨️", 86: "🌨️",
  95: "⛈️", 96: "⛈️", 99: "⛈️",
};
const weatherInfo = (code: number) => ({
  emoji: WEATHER_EMOJI[code] || "🌡️",
  label: WEATHER_EMOJI[code] ? t(`routeDetails.weatherCode.${code}`) : t("routeStatus.unknown"),
});

const advisory = (code: number) => {
  if ([71, 73, 75, 77, 85, 86].includes(code)) return t("routeDetails.advisory.snow");
  if ([51, 53, 55, 56, 57, 61, 63, 65, 66, 67, 80, 81, 82].includes(code)) return t("routeDetails.advisory.rain");
  if ([95, 96, 99].includes(code)) return t("routeDetails.advisory.storm");
  if ([45, 48].includes(code)) return t("routeDetails.advisory.fog");
  return t("routeDetails.advisory.none");
};

const dayLabel = (dateStr: string, idx: number) => {
  if (idx === 0) return t("routeDetails.day.today");
  if (idx === 1) return t("routeDetails.day.tomorrow");
  const d = new Date(dateStr);
  return d.toLocaleDateString(locale.value, { weekday: "short" });
};


const fetchWeather = async () => {
  const centroid = routeCentroid(props.route);
  days.value = [];
  if (!centroid) {
    loading.value = false;
    return;
  }
  loading.value = true;
  try {
    const params = new URLSearchParams({
      latitude: centroid.lat.toFixed(4),
      longitude: centroid.lng.toFixed(4),
      daily: "weathercode,temperature_2m_max,temperature_2m_min,windspeed_10m_max",
      timezone: "auto",
      forecast_days: "7",
    });
    // Обычный fetch (не инстанс `api`) — сторонний домен не должен получать наш Authorization-токен.
    const res = await fetch(`https://api.open-meteo.com/v1/forecast?${params}`);
    if (!res.ok) throw new Error("forecast request failed");
    const data = await res.json();
    const d = data.daily;
    days.value = (d?.time || []).map((date: string, i: number) => ({
      date,
      code: d.weathercode[i],
      tmin: d.temperature_2m_min[i],
      tmax: d.temperature_2m_max[i],
      windMax: d.windspeed_10m_max[i],
    }));
    selectedDayIdx.value = 0;
  } catch {
    days.value = [];
  } finally {
    loading.value = false;
  }
};

watch(() => props.route, fetchWeather, { immediate: true });
</script>
